import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { Button } from "@/components/ui/button";
import { SubmissionDetailSheet } from "@/features/submissions/components/SubmissionDetailSheet";
import { SubmissionsFilters } from "@/features/submissions/components/SubmissionsFilters";
import { SubmissionsTable } from "@/features/submissions/components/SubmissionsTable";
import { submissionService } from "@/features/submissions/services/submissionService";
import { useDepartments } from "@/features/departments/hooks/useDepartmentsQueries";
import { useScopedDepartments } from "@/features/reports/hooks/useReportScope";
import { ReportSectionError } from "@/features/reports/components/ReportSectionError";
import { ReportTableSkeleton } from "@/features/reports/components/ReportTableSkeleton";
import { API_ENDPOINTS } from "@/constants/api";
import { downloadAuthenticatedFile } from "@/lib/download-blob";
import { handleApiError } from "@/lib/error-handler";
import { queryKeys } from "@/lib/query-keys";
import type {
  ListSubmissionsQuery,
  Submission,
  SubmissionType,
} from "@/types/submission";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 350;

export default function SubmissionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<Submission | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const search = searchParams.get("search") ?? "";
  const type = (searchParams.get("type") ?? undefined) as
    | SubmissionType
    | undefined;
  const departmentId = searchParams.get("departmentId") ?? undefined;
  const hasAttachment = searchParams.get("hasAttachment") === "true";
  const page = Math.max(0, Number(searchParams.get("page") ?? "1") - 1);

  // The box is typed into locally and pushed to the URL after a pause, so a keystroke does not
  // fire a request and the filter still survives a refresh / shared link.
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);

  const patchParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (searchInput === search) return;
    const timer = window.setTimeout(
      () => patchParams({ search: searchInput || undefined, page: undefined }),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [searchInput, search, patchParams]);

  const { data: deptData } = useDepartments({ limit: 100 });
  const allDepartments = useMemo(() => deptData?.items ?? [], [deptData]);
  const departments = useScopedDepartments(allDepartments);

  const filters = useMemo<ListSubmissionsQuery>(
    () => ({
      type,
      departmentId,
      search: search || undefined,
      hasAttachment: hasAttachment || undefined,
      page: page + 1,
      pageSize: PAGE_SIZE,
    }),
    [type, departmentId, search, hasAttachment, page],
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.submissions.list(filters),
    queryFn: () => submissionService.list(filters),
  });

  const submissions = data?.items ?? [];
  const pageCount = Math.max(1, data?.meta.totalPages ?? 1);
  const hasActiveFilters = Boolean(
    search || type || departmentId || hasAttachment,
  );

  const setFilter = (updates: Record<string, string | undefined>) =>
    patchParams({ ...updates, page: undefined });

  useEffect(() => {
    if (page > 0 && page >= pageCount) {
      patchParams({ page: String(pageCount) });
    }
  }, [page, pageCount, patchParams]);

  async function onExport() {
    setIsExporting(true);
    try {
      await downloadAuthenticatedFile(
        API_ENDPOINTS.SUBMISSIONS.EXPORT,
        {
          type,
          departmentId,
          search: search || undefined,
          hasAttachment: hasAttachment ? "true" : undefined,
        },
        "submissions-export.csv",
      );
    } catch (err) {
      handleApiError(err, { customMessage: "تعذّر تصدير الطلبات" });
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <AdminPageShell
      title="الطلبات والاستفسارات"
      description="النماذج المُرسلة من الموقع، بما فيها المرفقات"
      isLoading={isLoading && !data}
      error={error}
      actions={
        <Button variant="outline" onClick={onExport} disabled={isExporting}>
          <Download className="ml-2 size-4" />
          {isExporting ? "جاري التصدير..." : "تصدير CSV"}
        </Button>
      }
    >
      <div className="space-y-4">
        <SubmissionsFilters
          search={searchInput}
          type={type}
          departmentId={departmentId}
          hasAttachment={hasAttachment}
          departments={departments}
          onSearchChange={setSearchInput}
          onTypeChange={(v) => setFilter({ type: v })}
          onDepartmentChange={(v) => setFilter({ departmentId: v })}
          onHasAttachmentChange={(v) =>
            setFilter({ hasAttachment: v ? "true" : undefined })
          }
          onClearFilters={() => {
            setSearchInput("");
            patchParams({
              search: undefined,
              type: undefined,
              departmentId: undefined,
              hasAttachment: undefined,
              page: undefined,
            });
          }}
          hasActiveFilters={hasActiveFilters}
        />

        {error ? (
          <ReportSectionError onRetry={() => refetch()} />
        ) : isLoading && !data ? (
          <ReportTableSkeleton columns={8} />
        ) : (
          <SubmissionsTable
            submissions={submissions}
            pageIndex={page}
            pageCount={pageCount}
            onPageChange={(p) =>
              patchParams({ page: p === 0 ? undefined : String(p + 1) })
            }
            onViewDetail={(s) => {
              setSelected(s);
              setDetailOpen(true);
            }}
          />
        )}
      </div>

      <SubmissionDetailSheet
        submissionId={selected?.id ?? null}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        preview={selected ?? undefined}
      />
    </AdminPageShell>
  );
}
